// Google Analytics 4 event helper.
//
// The store previously had NO GA4 ecommerce events at all — TagInjector fired
// gtag('config') and nothing else, so GA4 recorded a single pageview per session
// and zero revenue. Every report that matters for ad spend (purchases, funnel
// drop-off, product performance) was therefore empty.
//
// Like the Meta pixel, gtag is injected at runtime only after /settings/public
// resolves, so events fired during page load would land before gtag exists.
// Everything is buffered until it appears, then flushed in order.

const CURRENCY = 'INR';
const READY_TIMEOUT_MS = 15000;
const POLL_MS = 200;

let queue = [];
let polling = false;

const gtagReady = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

function flush() {
  const pending = queue;
  queue = [];
  pending.forEach(({ event, params }) => {
    try { window.gtag('event', event, params); } catch { /* ignore */ }
  });
}

function startPolling() {
  if (polling) return;
  polling = true;
  const startedAt = Date.now();
  const timer = setInterval(() => {
    if (gtagReady()) {
      clearInterval(timer);
      polling = false;
      flush();
    } else if (Date.now() - startedAt > READY_TIMEOUT_MS) {
      clearInterval(timer);
      polling = false;
      queue = []; // GA4 not configured — drop
    }
  }, POLL_MS);
}

export function gaEvent(event, params = {}) {
  try {
    if (typeof window === 'undefined') return;
    if (gtagReady()) {
      window.gtag('event', event, params);
      return;
    }
    queue.push({ event, params });
    startPolling();
  } catch { /* never let analytics break the page */ }
}

// Normalise our cart/product shapes into GA4's `items` array. Accepts either a
// bare id (legacy call sites) or a full object.
export function gaItems(items = []) {
  return items.map((i) => {
    if (i == null) return null;
    if (typeof i !== 'object') return { item_id: String(i) };
    return {
      item_id: String(i.product_id ?? i.id ?? ''),
      item_name: i.name || undefined,
      item_variant: i.variant_label || i.variant || undefined,
      price: Number(i.price) || 0,
      quantity: Number(i.qty ?? i.quantity) || 1,
    };
  }).filter(Boolean);
}

export function gaViewItem({ id, name, price } = {}) {
  if (id == null) return;
  gaEvent('view_item', {
    currency: CURRENCY,
    value: Number(price) || 0,
    items: [{ item_id: String(id), item_name: name, price: Number(price) || 0, quantity: 1 }],
  });
}

export function gaAddToCart({ id, name, price, qty = 1, variant } = {}) {
  if (id == null) return;
  gaEvent('add_to_cart', {
    currency: CURRENCY,
    value: (Number(price) || 0) * (Number(qty) || 1),
    items: [{
      item_id: String(id), item_name: name, item_variant: variant || undefined,
      price: Number(price) || 0, quantity: Number(qty) || 1,
    }],
  });
}

export function gaBeginCheckout({ items = [], value = 0 } = {}) {
  gaEvent('begin_checkout', {
    currency: CURRENCY,
    value: Number(value) || 0,
    items: gaItems(items),
  });
}

// De-duped per order, mirroring the pixel — a refresh on the thank-you page must
// not book the same revenue twice.
export function gaPurchase({ orderKey, items = [], value = 0, shipping = 0 } = {}) {
  try {
    if (orderKey) {
      const seen = JSON.parse(localStorage.getItem('yellove_ga_purchases') || '[]');
      if (seen.includes(String(orderKey))) return;
      seen.push(String(orderKey));
      localStorage.setItem('yellove_ga_purchases', JSON.stringify(seen.slice(-50)));
    }
  } catch { /* ignore storage errors, still fire below */ }

  gaEvent('purchase', {
    transaction_id: String(orderKey || ''),
    currency: CURRENCY,
    value: Number(value) || 0,
    shipping: Number(shipping) || 0,
    items: gaItems(items),
  });
}

// SPA route change. gtag('config') only sends a page_view for the very first
// load, so without this GA4 saw one page per session no matter how far the
// customer browsed.
export function gaPageView(path) {
  gaEvent('page_view', {
    page_path: path,
    page_location: typeof window !== 'undefined' ? window.location.href : undefined,
    page_title: typeof document !== 'undefined' ? document.title : undefined,
  });
}
